import { ORDER_QUEUE_TABS, ORDER_QUEUE_TAB_IDS } from './statuses';

/**
 * pageViewParams — WHICH view state is allowed into a page-view row, and in
 * what vocabulary.
 *
 * Added 2026-09-17. Owner ruling: *"I would like page visits to also be
 * included in activity so I can tell if they are visiting which pagges, which
 * tabs etc."*
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🚨 THE THING THE BRIEF ASSUMED, WHICH IS NOT TRUE: THE QUEUE TABS ARE NOT IN
 * THE URL.
 * ═══════════════════════════════════════════════════════════════════════════
 * `DepartmentOrders.js` holds its queue tab in plain React state
 * (`useState('all')`), and its own docblock says **no `persistKey` is passed**
 * to `ui/Tabs`, so it is not in `localStorage` either, and `hashSync` is not
 * used so it is not in the hash. `?status=` exists ONLY as a target
 * `AdminDashboard.js` navigates to (`/admin/orders?status=…`), and
 * `/admin/orders` is a `<Navigate to="/department/orders" replace />` — which
 * carries no search string, so the parameter is dropped in the redirect and
 * never lands anywhere.
 *
 * **So watching `location.search` records the tab NEVER.** That is why there
 * are TWO sources below: the URL, for params that genuinely exist, and a
 * DECLARED channel (`contexts/PageHeaderContext.js#usePageView`) for view state
 * a page holds itself. Only the second answers the owner's question.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🚨 THIS FILE IS NOT THE BOUND. `server/helpers/pageViewPath.js` IS.
 * ═══════════════════════════════════════════════════════════════════════════
 * Every allowed param here declares EITHER a closed `vocabulary` or a value
 * `shape`, and a value matching neither is DROPPED rather than sent. That stops
 * a search box putting free text on the wire — which matters, because
 * `activity_log.endpoint` is rendered on an admin screen and grouped by
 * `mapEndpointToPage`, so unbounded is a content-injection surface and an
 * unbounded-cardinality one in the same breath.
 *
 * 🔻 **BUT THE ENFORCEMENT IS ON THE SERVER, AND THIS DOCBLOCK CLAIMED OTHERWISE
 * UNTIL THE R16 REVIEW.** It said the shapes here were "bounded by construction"
 * in a way that read as a guarantee. Measured: LOOSEN the `dept` shape in this
 * file and the whole suite stays GREEN, because nothing pins the client copy's
 * strictness — `server/tests/regressions/page-view-params.test.js` §0 pins the
 * SERVER's anchoring and §3 pins only that the two KEY SETS match. Anyone signed
 * in can POST `/api/analytics/page-view` by hand anyway, so a guard living only
 * here was never more than a convention.
 *
 * 🔑 **So read this file as: what the client bothers to send.** The bound is
 * `sanitisePageViewPath`, which re-applies the allowlist AND bounds the path
 * part, and whose anchoring, prototype keys, injection probes and traversal
 * probes are all pinned there. Keep the two in step anyway — a client that sends
 * something the server discards is a request that buys nothing.
 */

// ── PUBLISHED VIEWS — pages that hand the logger their own view id ──────────
//
// 🔑 KEYED BY PATHNAME so the ratchet can ask the question the owner's request
// really poses: *does every page that publishes a view have a vocabulary and a
// label for it?* A page calling `usePageView` with no entry here publishes a
// token that is silently dropped — which looks exactly like the feature
// working, so `page-view-params.test.js` §2 fails instead.
export const PUBLISHED_VIEWS = {
  // 🔑 The vocabulary IS `ORDER_QUEUE_TAB_IDS`, never a copy of it — the tabs
  // and the analytics vocabulary are one list, so a seventh tab is covered
  // without anyone editing this file. Same for the labels.
  '/department/orders': {
    ids: ORDER_QUEUE_TAB_IDS,
    label: (id) => {
      const tab = ORDER_QUEUE_TABS.find((t) => t.id === id);
      return tab ? tab.label : null;
    },
  },
};

/** Every published view token, across every page. Bounded by construction. */
export const PUBLISHED_VIEW_IDS = Object.keys(PUBLISHED_VIEWS)
  .reduce((acc, p) => acc.concat(PUBLISHED_VIEWS[p].ids), []);

// ── ALLOWED, with a bounded vocabulary or shape ────────────────────────────
export const VIEW_PARAMS = {
  // The published-view key. Its vocabulary is the union above.
  tab: { vocabulary: PUBLISHED_VIEW_IDS },
  // `/budget/compare?dept=&version_a=&version_b=` — the only genuine view
  // params in a URL anywhere in the app today.
  // 🪤 SHAPE, not vocabulary: these are ids, so they cannot be enumerated —
  // but `^\d+$` still makes them incapable of carrying text.
  dept: { shape: /^\d+$/, label: 'Department' },
  version_a: { shape: /^\d+$/, label: 'Version A' },
  version_b: { shape: /^\d+$/, label: 'Version B' },
};

// ── DECLARED OUT, each with the reason ─────────────────────────────────────
// 🔑 Listed rather than merely absent: "why is `order` not here" is a question
// somebody will ask, and an unexplained absence invites them to add it.
export const EXCLUDED_PARAMS = [
  {
    param: 'order',
    why: 'A deep link to ONE record, and `hooks/useDeepLinkParam.js` STRIPS it '
      + 'immediately (setSearchParams replace) so a refresh cannot re-trigger it. '
      + 'Logging on search change would therefore write TWO rows per link — one '
      + 'on arrival, one when the strip empties the query — and neither is a view.',
  },
  {
    param: 'gag',
    why: 'Same shape as `order`: a record deep link on GagBreakdown, stripped by '
      + 'the same hook, so it would double-log and says nothing about which view.',
  },
  {
    param: 'hire',
    why: 'Same shape as `order` (2026-09-27): a record deep link to ONE hire line on '
      + 'DepartmentHires/MyHires, stripped by the same hook, so it would double-log '
      + 'and says nothing about which view.',
  },
  {
    param: 'teamId',
    why: 'Same shape again, on TeamManage — captured then deleted from the URL '
      + '(`next.delete(\'teamId\')`).',
  },
  {
    param: 'task',
    why: 'MyTeamTasks. It is NOT stripped, so it would not double-log — but it is '
      + 'still a deep link to ONE TASK rather than a view switch, and that page\'s '
      + 'real view (list/kanban/gantt) lives in localStorage, not the URL. '
      + 'Declared out as a record link, not for the stripping reason.',
  },
  {
    param: 'title',
    why: '🚨 FREE TEXT. RiskAssessmentBuilder pre-fills a new RA\'s title from it, so '
      + 'its value is whatever a user typed into the library. It is the single '
      + 'clearest reason this file is an ALLOWLIST and not a blocklist.',
  },
  {
    param: 'source',
    why: 'RiskAssessmentBuilder: which seed a new RA came from (`template`). A CREATE '
      + 'pre-fill, read once on mount, not a view the user switches between.',
  },
  {
    param: 'kind',
    why: 'RiskAssessmentBuilder, read only alongside `id` to build a template key — '
      + 'the `order`/`gag` record-reference case, in two parts.',
  },
  {
    param: 'id',
    why: 'RiskAssessmentBuilder, the other half of that template key: a record id.',
  },
  {
    param: 'returnUrl',
    why: 'Pre-auth only (/login), and it carries an arbitrary path — free text by '
      + 'definition, so it could never be bounded. Pre-auth paths are skipped by the '
      + 'logger anyway; this entry says it is a decision, not an accident of routing.',
  },
  {
    param: 'token',
    why: '🚨 A SECRET. Reset, verify and grading-approval links carry it. It must never '
      + 'reach a log row that is rendered on an admin screen; this entry exists so '
      + 'that stays a decision rather than an oversight.',
  },
];

/** Is this (param, value) pair allowed into a stored page-view row? */
export function isAllowedViewValue(param, value) {
  // 🪤 `hasOwnProperty`, not a bare lookup: `VIEW_PARAMS['constructor']` is
  // truthy on any plain object, so `if (!spec)` would wave a prototype key
  // through to the shape/vocabulary tests below.
  const spec = Object.prototype.hasOwnProperty.call(VIEW_PARAMS, param)
    ? VIEW_PARAMS[param] : null;
  if (!spec) return false;
  if (typeof value !== 'string' || value === '') return false;
  if (spec.vocabulary) return spec.vocabulary.indexOf(value) !== -1;
  if (spec.shape) return spec.shape.test(value);
  return false;
}

/**
 * Human label for a stored view token, for the activity feed.
 * Returns `null` when nothing claims it — never `''`, and never the raw token.
 */
export function viewTokenLabel(path, param, value) {
  if (param === 'tab') {
    const page = PUBLISHED_VIEWS[path];
    return page ? page.label(value) : null;
  }
  const spec = VIEW_PARAMS[param];
  return spec && spec.label ? `${spec.label} ${value}` : null;
}

/**
 * Build the canonical, bounded view suffix for a page-view row.
 *
 * 🔑 IT IS A QUERY STRING ON THE STORED PATH, AND THAT IS WHAT KEEPS THE SERVER
 * WORKING UNCHANGED. `helpers/analyticsEndpointMap.js#pathPartOf` splits a PAGE
 * row at the first space (the title) and then at `?`, so `mapEndpointToPage`,
 * `mapEndpointToFeature` and `normaliseEndpointForGrouping` all keep seeing
 * `/department/orders` exactly as before — the tab cannot fragment the charts.
 *
 * @param {URLSearchParams|null} search — the real URL params
 * @param {string|null} publishedView   — the view id the page published
 * @returns {string} '' or '?tab=to-receive' or '?dept=3&version_a=7'
 */
export function buildViewSuffix(search, publishedView) {
  const pairs = [];
  if (publishedView != null && isAllowedViewValue('tab', String(publishedView))) {
    pairs.push(['tab', String(publishedView)]);
  }
  if (search && typeof search.forEach === 'function') {
    search.forEach((value, key) => {
      if (key === 'tab') return; // the published view owns this key
      if (isAllowedViewValue(key, value)) pairs.push([key, value]);
    });
  }
  if (pairs.length === 0) return '';
  // 🪤 SORTED. Two visits to one view must produce the same string, or
  // `?a=1&b=2` and `?b=2&a=1` are two rows for one view and the
  // no-duplicate-consecutive guard silently stops working.
  pairs.sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
  return `?${pairs.map(([k, v]) => `${k}=${v}`).join('&')}`;
}
